import React, { useEffect, useMemo, useRef, useState } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import useQueryGetMe from '@/api/auth/getMe.ts'
import SolidButton from '@/component/button/SolidButton.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import { useNavigate, Link } from 'react-router-dom'
import ChangePasswordModal from '@/pages/account/ChangePasswordModal.tsx'
import EditUserModal from '@/pages/account/EditUserModal.tsx'
import AddUserModal from '@/pages/account/AddUserModal.tsx'
import AdminThumbnail from '@/component/image/AdminThumbnail.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetUsers, { IUser } from '@/api/user/getUsers.ts'
import { apiDeleteUser } from '@/api/user/deleteUser.ts'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { APP_VERSION } from '@/constant/constant.ts'
import Pagination from '@/component/pagination/Pagination.tsx'
import SearchBar from '@/component/input/SearchBar.tsx'
import useDebounce from '@/util/hook/useDebounce.tsx'
import useQueryGetMyPermission, {
    PermissionAction,
    PermissionSubject
} from '@/api/auth/getPermission.ts'
import useQueryGetActiveTestSessions from '@/api/user/getActiveTestSessions.ts'
import ClockIcon from '@/assets/ic_clock.svg?react'

type IProps = {}

const PAGE_SIZE = 10

export type IEditAccount = {
    name?: string
    isNameEditing: boolean
    contact: string
    isContactEditing: boolean
    representerName: string
    isRepresenterNameEditing: boolean
    representerContact: string
    isRepresenterContactEditing: boolean
    representerEmail: string
    isRepresenterEmailEditing: boolean
    file?: File
}

const calculateAge = (birthDate: string): number => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
    }
    return age
}

const formatBirthDate = (birthDate: string): string => {
    const date = new Date(birthDate)
    if (isNaN(date.getTime())) return birthDate
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const Account: React.FC<IProps> = ({}) => {
    const { data: center } = useQueryGetMe()
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounce(search, 500)
    const { data: users, isLoading: isUsersLoading } = useQueryGetUsers(
        {
            pageNum: page,
            pageSize: PAGE_SIZE
        },
        debouncedSearch
    )
    const navigate = useNavigate()
    const changePasswordModalRef = useRef<any>()
    const editUserModalRef = useRef<any>()
    const addUserModalRef = useRef<any>()
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const { hasPermission } = useQueryGetMyPermission()
    const { data: activeTestSessions } = useQueryGetActiveTestSessions()
    const testingProtecteeIds = useMemo(
        () => new Set((activeTestSessions ?? []).map(session => session.protecteeId)),
        [activeTestSessions]
    )

    const canEditProtectee = hasPermission(
        PermissionSubject.ALERT_EMERGENCY,
        PermissionAction.UPDATE
    )
    const canDeleteProtectee = hasPermission(
        PermissionSubject.ALERT_EMERGENCY,
        PermissionAction.DELETE
    )

    const userList = users?.data ?? []
    const pagination = users?.pagination

    useEffect(() => {
        setPage(1)
    }, [debouncedSearch])

    const onClickEdit = (user: IUser) => {
        editUserModalRef.current?.show(user)
    }

    const onClickDelete = async (user: IUser) => {
        const confirmed = window.confirm(t('account.protectee.delete.confirm', { name: user.name }))
        if (!confirmed) return

        try {
            const { code } = await apiDeleteUser(user.id)
            if (code === 200) {
                toast.success(t('account.protectee.delete.success'))
                await queryClient.invalidateQueries({
                    queryKey: QueryCacheKeys.user.getUsersRoot()
                })
            } else {
                toast.error(t('account.protectee.delete.error'))
            }
        } catch {
            toast.error(t('account.protectee.delete.error'))
        }
    }

    return (
        <>
            <PageConfig className={'account-page'} showSideNav={true} showTopNav={true}>
                <div className={'section_1200 mt-[20px]'}>
                    {/* 프로필 영역 */}
                    <div className={'min-w-[170px] max-w-[300px] mx-auto'}>
                        <h1 className={'text-heading1 text-center mt-[40px]'}>
                            {t('account.title')}
                        </h1>
                        <div className={'grid mt-[30px]'}>
                            <div className={'flex justify-center'}>
                                <AdminThumbnail
                                    className={''}
                                    src={center?.profileUrl}
                                    imageClassName={'w-[100px] h-[100px]'}
                                />
                            </div>
                            <div className={'grid mt-[24px]'}>
                                <BorderButton
                                    size={'small'}
                                    text={t('account.changePassword')}
                                    className={'w-full'}
                                    onClick={() => changePasswordModalRef.current?.show()}
                                />
                                <p className={'mt-[10px] text-[14px] text-center'}>
                                    {t('account.forgotPassword.desc')}
                                </p>
                                <p className={'text-[14px] text-center'}>
                                    {t('account.forgotPassword.desc.phone')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* 회원 리스트 영역 */}
                    <div className={'mt-[48px] mb-[40px] max-w-[1200px] mx-auto px-[16px]'}>
                        <div className={'flex items-center justify-between mb-[16px]'}>
                            <h2 className={'text-[18px] font-bold'}>
                                {t('account.protectee.title')}
                                {!!pagination?.totalEntries && (
                                    <span
                                        className={'text-[14px] font-normal text-gray-500 ml-[8px]'}
                                    >
                                        ({pagination.totalEntries}
                                        {t('account.protectee.count')})
                                    </span>
                                )}
                            </h2>
                            <div className={'flex items-center gap-[8px]'}>
                                <SearchBar
                                    className={'w-[220px] rounded-[5px] overflow-hidden'}
                                    inputParams={{
                                        placeholder: t('users.list.search.placeholder')
                                    }}
                                    onChanged={(text) => setSearch(text)}
                                />
                                <SolidButton
                                    size={'small'}
                                    text={t('account.protectee.add.button')}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        <div className={'border border-gray-200 rounded-lg overflow-x-auto'}>
                            <table className={'w-full min-w-[1100px]'}>
                                <thead>
                                    <tr className={'bg-gray-50 border-b border-gray-200'}>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'
                                            }
                                        >
                                            {t('account.protectee.table.name')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'
                                            }
                                        >
                                            {t('users.detail.gender')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'
                                            }
                                        >
                                            {t('users.detail.birthDate')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'
                                            }
                                        >
                                            {t('account.protectee.table.age')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'
                                            }
                                        >
                                            {t('account.protectee.table.contact')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'
                                            }
                                        >
                                            {t('users.detail.address')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'
                                            }
                                        >
                                            {t('account.protectee.table.deviceSerial')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'
                                            }
                                        >
                                            {t('users.detail.contact.guardian')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-left whitespace-nowrap'
                                            }
                                        >
                                            {t('users.detail.memo.title')}
                                        </th>
                                        <th
                                            className={
                                                'px-[12px] py-[10px] text-[13px] font-semibold text-gray-600 text-center whitespace-nowrap'
                                            }
                                        >
                                            {t('account.protectee.table.actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isUsersLoading && (
                                        <tr>
                                            <td
                                                colSpan={10}
                                                className={
                                                    'px-[16px] py-[40px] text-center text-[14px] text-gray-400'
                                                }
                                            >
                                                {t('common.loading')}
                                            </td>
                                        </tr>
                                    )}

                                    {!isUsersLoading && userList.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={10}
                                                className={
                                                    'px-[16px] py-[40px] text-center text-[14px] text-gray-400'
                                                }
                                            >
                                                {debouncedSearch
                                                    ? t('users.list.empty.search')
                                                    : t('account.protectee.empty')}
                                            </td>
                                        </tr>
                                    )}

                                    {!isUsersLoading &&
                                        userList.map((user: IUser) => (
                                            <tr
                                                key={user.id}
                                                className={`border-b border-gray-100 last:border-b-0 transition-colors ${
                                                    testingProtecteeIds.has(user.id)
                                                        ? 'bg-[#fff4e9] hover:bg-[#ffeedd]'
                                                        : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-900 whitespace-nowrap'
                                                    }
                                                >
                                                    <span className={'inline-flex items-center gap-[4px]'}>
                                                        {user.name}
                                                        {testingProtecteeIds.has(user.id) && (
                                                            <ClockIcon
                                                                role={'img'}
                                                                aria-label={t('testMode.list.testing')}
                                                                className={'h-[14px] w-[14px] flex-none text-[#bd641f]'}
                                                            />
                                                        )}
                                                    </span>
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-700 text-center whitespace-nowrap'
                                                    }
                                                >
                                                    {user.gender === 0
                                                        ? t('common.gender.male')
                                                        : user.gender === 1
                                                          ? t('common.gender.female')
                                                          : '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-700 text-center whitespace-nowrap'
                                                    }
                                                >
                                                    {user.birthDate
                                                        ? formatBirthDate(user.birthDate)
                                                        : '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-700 text-center whitespace-nowrap'
                                                    }
                                                >
                                                    {user.birthDate
                                                        ? t('account.protectee.table.ageValue', {
                                                              age: calculateAge(user.birthDate)
                                                          })
                                                        : '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-700 whitespace-nowrap'
                                                    }
                                                >
                                                    {user.contact || '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-500 max-w-[180px] truncate'
                                                    }
                                                    title={user.address}
                                                >
                                                    {user.address || '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-500 font-mono whitespace-nowrap'
                                                    }
                                                >
                                                    {user.device?.device_serial || '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-700 whitespace-nowrap'
                                                    }
                                                >
                                                    {[user.primaryRelation, user.primaryContact]
                                                        .filter(Boolean)
                                                        .join(' ') || '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] text-[14px] text-gray-500 max-w-[150px] truncate'
                                                    }
                                                    title={user.inspectorMemo}
                                                >
                                                    {user.inspectorMemo || '-'}
                                                </td>
                                                <td
                                                    className={
                                                        'px-[12px] py-[10px] whitespace-nowrap'
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            'flex items-center justify-center gap-[6px]'
                                                        }
                                                    >
                                                        {canEditProtectee && (
                                                            <button
                                                                className={
                                                                    'text-[12px] px-[10px] py-[5px] bg-main_b text-white rounded hover:opacity-80 transition-opacity'
                                                                }
                                                                onClick={() => onClickEdit(user)}
                                                            >
                                                                {t('account.protectee.table.edit')}
                                                            </button>
                                                        )}
                                                        {canDeleteProtectee && (
                                                            <button
                                                                className={
                                                                    'text-[12px] px-[10px] py-[5px] bg-white text-red-500 border border-red-300 rounded hover:bg-red-50 transition-colors'
                                                                }
                                                                onClick={() => onClickDelete(user)}
                                                            >
                                                                {t(
                                                                    'account.protectee.table.delete'
                                                                )}
                                                            </button>
                                                        )}
                                                        {!canEditProtectee &&
                                                            !canDeleteProtectee && (
                                                                <span
                                                                    className={
                                                                        'text-[13px] text-gray-400'
                                                                    }
                                                                >
                                                                    -
                                                                </span>
                                                            )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {!!pagination && pagination.totalPages > 1 && (
                            <Pagination
                                className={'mt-[20px]'}
                                page_size={pagination.pageSize}
                                current_page={pagination.currentPage}
                                total_page={pagination.totalPages}
                                fetchPage={setPage}
                            />
                        )}
                    </div>

                    {/* 버전 정보 */}
                    <div className={'mt-[48px] max-w-[1200px] mx-auto px-[16px]'}>
                        <h2 className={'text-[18px] font-bold mb-[16px]'}>
                            {t('account.product.title')}
                        </h2>
                        <div className={'border border-gray-200 rounded-lg'}>
                            <div
                                className={
                                    'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4'
                                }
                            >
                                <span>{t('account.product.version')}</span>
                                <span className={'text-gray3'}>{APP_VERSION}</span>
                            </div>
                        </div>
                    </div>

                    {/* 약관 및 정책 */}
                    <div className={'mt-[48px] mb-[40px] max-w-[1200px] mx-auto px-[16px]'}>
                        <h2 className={'text-[18px] font-bold mb-[16px]'}>
                            {t('account.terms.title')}
                        </h2>
                        <div
                            className={'border border-gray-200 rounded-lg divide-y divide-gray-200'}
                        >
                            <Link
                                to={'/terms'}
                                className={
                                    'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4 hover:bg-gray-50 transition-colors'
                                }
                            >
                                <span>{t('account.terms.service')}</span>
                                <span className={'text-gray3'}>&gt;</span>
                            </Link>
                            <Link
                                to={'/privacy'}
                                className={
                                    'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4 hover:bg-gray-50 transition-colors'
                                }
                            >
                                <span>{t('account.terms.privacy')}</span>
                                <span className={'text-gray3'}>&gt;</span>
                            </Link>
                            <Link
                                to={'/location-terms'}
                                className={
                                    'flex items-center justify-between px-[16px] py-[14px] text-[14px] text-gray4 hover:bg-gray-50 transition-colors'
                                }
                            >
                                <span>{t('account.terms.location')}</span>
                                <span className={'text-gray3'}>&gt;</span>
                            </Link>
                        </div>
                    </div>

                    {/* 확인 버튼 */}
                    <div className={'max-w-[300px] mx-auto mb-[60px]'}>
                        <SolidButton
                            text={t('account.confirm')}
                            className={'w-full'}
                            onClick={() => navigate(-1)}
                        />
                    </div>
                </div>
            </PageConfig>
            <ChangePasswordModal ref={changePasswordModalRef} />
            <EditUserModal ref={editUserModalRef} />
            <AddUserModal ref={addUserModalRef} centerId={center?.id} />
        </>
    )
}

export default Account
